import { getErrorMessage } from '@utils/error-message';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  DetailContext,
  DetailHandle,
  EvidenceContext,
  NavigationContext,
  PrimaryDetailActionbarButton,
  SecondaryDetailActionbarButton,
} from '@eas/common-web';
import { useCancelDecisionDialog } from '@modules/document/dialog-actions/cancel-decision-hook';
import { useCancelDialog } from '@modules/document/dialog-actions/cancel-hook';
import { useReopenDialog } from '@modules/document/dialog-actions/reopen-hook';
import { useInternalDocumentPermission } from '@modules/document/document-permission';
import { useAddTaskDialog } from '@composite/tasks/dialog-actions/add-task-hook';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { InternalDocument, Task } from '@models';
import {
  EvidenceBrowserUrl,
  Messages,
  TaskState,
  TaskType,
  UIElement,
} from '@enums';

const useStyles = makeStyles(() => ({
  wrapper: {
    flexWrap: 'wrap',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginRight: 25,
  },
}));

export function ActionBar() {
  const classes = useStyles();
  const { navigate } = useContext(NavigationContext);
  const { tableRef } = useContext(EvidenceContext);
  const { source } = useContext<DetailHandle<InternalDocument>>(DetailContext);

  const intl = useIntl();

  const { callApi: cancelApiCall, showButton: showCancelButton } =
    useCancelDialog();

  const {
    callApi: cancelDecisionApiCall,
    showButton: showCancelDecisionButton,
  } = useCancelDecisionDialog();

  const { hasPermission } = useInternalDocumentPermission();

  const { AddTaskButton, disabledBtn: disabledAddTaskBtn } = useAddTaskDialog();
  const { showButton: showReopenButton, ReopenButton } = useReopenDialog();

  return (
    <div className={classes.wrapper}>
      <div>
        <AddTaskButton
          buttonLabel={intl.formatMessage({
            id: 'ES__INTERNAL_DOCUMENTS__ACTIONBAR__ADD_TASK',
            defaultMessage: 'Nová úloha',
          })}
          ButtonComponent={PrimaryDetailActionbarButton}
          formInitialValues={
            {
              esFile: source.data?.esFile,
              type: TaskType.CUSTOM,
              state: TaskState.SOLVING,
              document: source.data,
            } as Task
          }
          disabled={disabledAddTaskBtn}
        />
        {showReopenButton && (
          <ReopenButton
            buttonDisabled={
              !hasPermission(
                UIElement.InternalDocument.REOPEN_INTERNAL_DOCUMENT_BUTTON
              )
            }
          />
        )}
      </div>
      <div>
        {showCancelDecisionButton && (
          <DetailActionButton
            promptKey="CANCEL_DECISION"
            apiCall={cancelDecisionApiCall}
            buttonLabel={intl.formatMessage({
              id: 'ES__DOCUMENTS__ACTIONBAR__CANCEL_DECISION',
              defaultMessage: 'Stornovat rozhodnutí',
            })}
            dialogTitle={intl.formatMessage({
              id: 'ES__DOCUMENTS__DIALOG_CANCEL_DECISION__TITLE',
              defaultMessage: 'Storno rozhodnutí',
            })}
            dialogText={intl.formatMessage({
              id: 'ES__DOCUMENTS__DIALOG_CANCEL_DECISION__TEXT',
              defaultMessage:
                'Skutečně chcete stornovat navázané rozhodnutí? Dokument zůstane beze změny.',
            })}
            errorMessage={(err) =>
              getErrorMessage(err, Messages.Decision.CANCEL_ERROR)[0]
            }
            ButtonComponent={SecondaryDetailActionbarButton}
            buttonDisabled={
              !hasPermission(UIElement.InternalDocument.CANCEL_DECISION_BUTTON)
            }
            onSuccess={async () => {
              await source.refresh();
              tableRef.current?.refresh();
            }}
          />
        )}
        {showCancelButton && (
          <DetailActionButton
            promptKey="CANCEL"
            apiCall={cancelApiCall}
            buttonLabel={intl.formatMessage({
              id: 'ES__OUTGOING_DOCUMENTS__ACTIONBAR__CANCEL',
              defaultMessage: 'Stornovat',
            })}
            dialogTitle={intl.formatMessage({
              id: 'ES__OUTGOING_DOCUMENTS__DIALOG_CANCEL__TITLE',
              defaultMessage: 'Storno',
            })}
            dialogText={intl.formatMessage({
              id: 'ES__OUTGOING_DOCUMENT__DIALOG_CANCEL__TEXT',
              defaultMessage: 'Skutečně chcete stornovat dokument?',
            })}
            ButtonComponent={SecondaryDetailActionbarButton}
            buttonDisabled={
              !hasPermission(UIElement.InternalDocument.CANCEL_BUTTON)
            }
            onSuccess={async () => {
              tableRef.current?.refresh();

              navigate(EvidenceBrowserUrl.DOCUMENTS_INTERNAL, true);
            }}
          />
        )}
      </div>
    </div>
  );
}
